import classnames from 'classnames'
import PropTypes from 'prop-types'
import { createRef, PureComponent } from 'react'
import Dropzone from 'react-dropzone'
import ReactPlayer from 'react-player/lazy'
import { connect } from 'react-redux'
import TextareaAutosize from 'react-textarea-autosize'
import { Button, Form, Icon, Image, Message } from 'semantic-ui-react'

import APP from 'app'
import { Markdown } from 'ui'

import SimpleButton from 'ui/src/components/SimpleButton'

import { postListAdd, postListAddCanceled } from '../PostListActions'

import './PostAdd.css'

const PARSE_POST_TIMEOUT_MS = 1200

const isFacebookVideoURL = (URL) => URL.match(/(https?:\/\/\S*\.?facebook\.com\/\S+)/g)

const isVideoURL = (URL) =>
  URL.match(/(https?:\/\/\S*\.?youtube\.com\/\S+)/g) ||
  URL.match(/(https?:\/\/\S*\.?youtu\.be\/\S+)/g) ||
  URL.match(/(https?:\/\/\S*\.?dailymotion\.com\/\S+)/g) ||
  URL.match(/(https?:\/\/\S*\.?twitch\.tv\/\S+)/g) ||
  URL.match(/(https?:\/\/\S*\.?vimeo\.com\/\S+)/g) ||
  URL.match(/(https?:\/\/\S*\.?giphy\.com\/\S+\.mp4)/g) ||
  URL.match(/(https?:\/\/\S*\.?facebook\.com\/\S+)/g)

const isAudioURL = (URL) => URL.match(/(https?:\/\/\S*\.?soundcloud.com\/\S+)/g)

class PostAdd extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      error: null,
      files: {},
      focused: false,
      post: '',
      previewMode: false,
      updating: false,
      urls: [],
    }
    this.dropzoneRef = createRef()
    this.parsePostTimeout = null
  }

  static getDerivedStateFromProps(props, state) {
    const newState = {
      lastPostId: props.postId,
    }

    if (state.updating) {
      if (props.error) {
        newState.error = props.error.msg
        newState.updating = false
      } else if (state.lastPostId !== props.postId) {
        newState.error = null
        newState.files = {}
        newState.post = ''
        newState.updating = false
      }
    }

    return newState
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.post !== this.state.post) {
      clearTimeout(this.parsePostTimeout)
      this.parsePostTimeout = setTimeout(this.parsePost, PARSE_POST_TIMEOUT_MS)
    }
  }

  componentWillUnmount() {
    clearTimeout(this.parsePostTimeout)
  }

  onDrop = (acceptedFiles) =>
    this.setState((prevState) => {
      const files = {}

      Object.keys(prevState.files).forEach((fileName) => URL.revokeObjectURL(prevState.files[fileName].preview))

      acceptedFiles.slice(0, 2).forEach((file) => {
        Object.assign(file, { preview: URL.createObjectURL(file) })
        files[file.name] = file
      })

      return {
        error: null,
        files,
      }
    })

  onFormSubmit = (event) => {
    event.preventDefault()
    if (!this.state.updating) {
      if (this.state.post) {
        this.setState({
          error: null,
          previewMode: false,
          updating: true,
        })
        this.props.postListAdd({
          images: this.state.files,
          message: this.state.post,
          type: this.props.type,
          typeId: this.props.owner.id,
          url: this.state.urls?.[0] || '',
          version: 2,
          wall: this.props.wall,
        })
      } else {
        this.setState({
          error: 'Debes agregar texto al post.',
          previewMode: false,
        })
      }
    }
  }

  onOpenUploadDialog = () => !!this.dropzoneRef.current && this.dropzoneRef.current.open()

  onRemovePreview = ({ currentTarget: { value } }) =>
    this.setState((prevState) => {
      const files = { ...prevState.files }
      const urls = prevState.urls.filter((url) => url !== value)
      delete files[value]

      return {
        error: null,
        files,
        urls,
      }
    })

  onTextareaBlur = () => !this.state.post && this.setState({ focused: false })

  onTextareaChange = ({ target: { value } }) =>
    this.setState({
      error: null,
      post: value,
    })

  onTextareaFocus = () => this.setState({ focused: true })

  parsePost = () =>
    this.setState((prevState) => {
      const urls = (prevState.post.match(/([^\S]|^)(((https?:\/\/)|(www\.))(\S+))/gi) || [])
        .map((url) => {
          let URL = url.trim()
          if (!URL.startsWith('http')) {
            URL = `https://${URL}`
          }
          return URL
        })
        .filter((v, i, a) => a.indexOf(v) === i)
        .filter((url) => isVideoURL(url) || isAudioURL(url))
        .slice(0, 2)

      if (urls.toString() !== prevState.urls.toString()) {
        return { urls }
      }
      return null
    })

  twoFacebookVideoPreview = (url) => this.state.urls.length > 1 && isFacebookVideoURL(url)

  renderPreview = (name, content) => (
    <div key={name} className="PostAdd__preview">
      <div>{content}</div>
      <div>
        <SimpleButton
          ariaLabel="Eliminar Imagen"
          icon="trash"
          itemsDirection="rtl"
          onClick={this.onRemovePreview}
          value={name}
        >
          Eliminar
        </SimpleButton>
      </div>
    </div>
  )

  render() {
    const { error, files, focused, post, updating, urls } = this.state
    const { large, owner, type, user } = this.props

    if (!user) {
      return null
    }

    const avatar = (() => {
      if (type === 'institution') {
        if (owner.profile && owner.profile.avatar && owner.profile.avatar.filename) {
          owner.avatar = owner.profile.avatar.filename
        }

        return owner.avatar
          ? `${APP.services}/images/45x45-fit/${owner.avatar.filename || owner.avatar}`
          : `${APP.url}/img/castle.png`
      }
      return user.avatar
        ? `${APP.services}/images/45x45-fit/${user.avatar.filename || user.avatar}`
        : `${APP.url}/img/profile.jpg`
    })()

    const inEdition = focused || Object.keys(this.state.files).length > 0

    return (
      <div
        className={classnames(
          'PostAdd bg-white border border-greyLight rounded-lg p-2',
          inEdition && 'PostAdd-focused',
        )}
      >
        <Dropzone
          accept={{ 'image/gif': ['.gif'], 'image/jpeg': ['.jpg', '.jpeg'], 'image/png': ['.png'] }}
          noClick
          noKeyboard
          onDrop={this.onDrop}
          ref={this.dropzoneRef}
        >
          {({ getInputProps, getRootProps, isDragActive, isDragReject }) => (
            <div
              {...getRootProps({
                className: classnames('PostAdd__zone', isDragActive && 'active', isDragReject && 'reject'),
              })}
            >
              <input {...getInputProps()} />
              <Form key="mini" as="form" error={!!error} onSubmit={this.onFormSubmit} size="large">
                <div className="PostAdd__avatar">
                  <Image avatar className="big" src={avatar} />
                </div>
                <div className="PostAdd__post">
                  {this.state.previewMode ? (
                    <div className="border p-2">
                      <Markdown>{post}</Markdown>
                    </div>
                  ) : (
                    <TextareaAutosize
                      className={classnames('PostAdd__text', { focused: inEdition })}
                      onBlur={this.onTextareaBlur}
                      onChange={this.onTextareaChange}
                      onFocus={this.onTextareaFocus}
                      placeholder="Compartir"
                      rows={1}
                      value={post}
                    />
                  )}
                  {inEdition && (
                    <div className="relative h-4">
                      <div className="absolute left-0 top-0 text-sm flex">
                        <div
                          className={classnames(
                            'border-x border-b px-2 py-0.5 rounded-b-md text-greyDark cursor-pointer',
                            inEdition ? 'bg-greyLightest' : 'bg-white opacity-70',
                          )}
                          onClick={() => this.setState({ previewMode: false })}
                          onKeyDown={() => this.setState({ previewMode: false })}
                          role="button"
                          tabIndex={0}
                        >
                          Editar
                        </div>
                        <div
                          className={classnames(
                            'border-x border-b px-2 py-0.5 rounded-b-md text-greyDark cursor-pointer',
                            inEdition ? 'bg-white opacity-70' : 'bg-greyLightest',
                          )}
                          onClick={() => this.setState({ previewMode: true })}
                          onKeyDown={() => this.setState({ previewMode: true })}
                          role="button"
                          tabIndex={0}
                        >
                          Ver
                        </div>
                      </div>
                    </div>
                  )}
                  {!inEdition && (
                    <Icon
                      className="PostAdd__image_add"
                      color="green"
                      name="image"
                      onClick={this.onOpenUploadDialog}
                      size="big"
                    />
                  )}
                  {!!error && <Message content={error} error header="Error" size="mini" />}
                </div>
                {inEdition && (
                  <div className="PostAdd__action">
                    <div className="PostAdd__images">
                      {Object.keys(files).map((fileName) =>
                        this.renderPreview(
                          fileName,
                          <img
                            alt={fileName}
                            src={files[fileName].preview}
                            style={{
                              maxHeight: 160 / Object.keys(files).length,
                              maxWidth: 160 / Object.keys(files).length,
                            }}
                          />,
                        ),
                      )}
                      {urls.map((url) =>
                        this.renderPreview(
                          url,
                          <ReactPlayer
                            className={classnames(
                              this.twoFacebookVideoPreview(url) && 'PostAdd__facebookVideo',
                              !large && this.twoFacebookVideoPreview(url) && 'PostAdd__facebookVideo_compact',
                            )}
                            controls
                            height={(260 / urls.length) * (9 / 16)}
                            light={!large && this.twoFacebookVideoPreview(url)}
                            url={url}
                            width={260 / urls.length}
                          />,
                        ),
                      )}
                    </div>
                    {Object.keys(files).length + urls.length > 1 && (
                      <small>
                        <em>Solo el primer elemento será agregado.</em>
                      </small>
                    )}
                    <div>
                      <Icon
                        className="PostAdd__image_add"
                        color="green"
                        name="image"
                        onClick={this.onOpenUploadDialog}
                        size="big"
                      />
                      <Button
                        className={classnames('PostAdd__button', 'small', {
                          disabled: updating,
                          loading: updating,
                          primary: !updating,
                        })}
                        onClick={this.onFormSubmit}
                        type="submit"
                      >
                        Publicar
                      </Button>
                    </div>
                  </div>
                )}
              </Form>
            </div>
          )}
        </Dropzone>
      </div>
    )
  }
}

PostAdd.propTypes = {
  owner: PropTypes.object.isRequired,
  postListAdd: PropTypes.func.isRequired,

  error: PropTypes.object,
  large: PropTypes.bool,
  postId: PropTypes.string,
  type: PropTypes.oneOf([
    'advertising',
    'institution',
    'sports_soccer_match',
    'sports_soccer_team',
    'sports_tournament',
    'stats',
    'user',
  ]).isRequired,
  user: PropTypes.object,
  wall: PropTypes.bool,
}

PostAdd.defaultProps = {
  error: null,
  large: false,
  postId: null,
  user: null,
  wall: false,
}

export class Pure extends PostAdd {}

export default connect(
  ({ auth: { usr }, environment, posts }) => ({
    error: posts.error,
    large: !!environment.stage && environment.stage.width > 600,
    postId: posts.data && posts.data[0] ? posts.data[0].id : null,
    user: usr,
  }),
  {
    postListAdd,
    postListAddCanceled,
  },
)(PostAdd)
